/**
 * The Frame picker's own reading of the URL — FRAME_PICKER_DESIGN.md §6.1, §6.4.
 *
 * The generic helpers live in `utils/route-state.ts` and return raw strings; the validation
 * lives HERE, because the owner's ruling is that the two editors on these pages share leaf
 * pieces and nothing else: no shared `activeTab` union, no shared `readEditorTab`, each page
 * validating the tab string against its own set. The Studio's reading is
 * `components/studio/state/studio-url.ts`; this module never imports it and never writes or
 * clears the Studio's keys (`tab=studio`, `sid`, `step`, `job`).
 *
 * The picker owns exactly three query keys:
 *   `tab=picker`  — the tab is open (its absence IS Quick convert, §6.4)
 *   `psid`        — the picker session id; the storage key is `FRAMEPICKER-<psid>`
 *   `pstep`       — `check` while the big-job check is showing (§6.5); absent means editing
 *
 * The writes §6.4 fixes, and which kind each is:
 *   open the tab      push     {tab}          — Back closes the picker (the page base's click)
 *   session known     replace  {psid}         — Back must not step through the id write
 *   big-job check     push     {pstep:'check'} — Back returns to the editor
 *   close the tab     push     {tab,psid,pstep: null}
 *   done              Router.push('/downloads')  (the export module owns that one)
 * `t` (the playhead) is never written: selection is not history.
 */
import { ourEdgeHost } from '../../../utils/edge-owner';
import { pushQuery, readQuery, replaceQuery } from '../../../utils/route-state';
import type { PickerOpenSource } from '../events';

/** The picker's tab value. The Studio has its own; neither knows about the other. */
export const PICKER_TAB_ID = 'picker';

export type PickerStep = 'edit' | 'check';

export interface PickerUrlState {
    tab: string | null;
    psid: string | null;
    pstep: PickerStep;
    /**
     * IT1 (W3, saas #2): HOW the tab was opened -- `psrc`, written by `openPickerTab` (the page's tab click, the nudge,
     * the "Pick it on the video" link, the /downloads link) and CONSUMED by the root's first `replacePickerSession`
     * (`psrc: null`), so `FRAMEPICKER_OPENED{source}` is emitted once per open and never again on a reload. Absent on
     * a URL that carries `tab=picker` from a cold load: that is `source: 'url'`.
     */
    psrc: PickerOpenSource | null;
    /**
     * Editor re-open T3 deviation → wave B: `pfresh=1` asks for an EMPTY picker session -- no `psid`, and the page's LATEST
     * record is NOT restored (`root/session.ts#resolveSession` mints a new sid). Written by the expired /downloads card's one
     * action ("Pick frames from a new video"); consumed with the first `psid` write (`replacePickerSession`). Absent = false.
     */
    pfresh?: boolean;
    /**
     * Editor re-open T4: `pwf=<workflow uuid>` (+ `pdom=<processDomain>`, the card's edge; `psid` = the card's workflow id) -- the /downloads card's Re-edit when this
     * browser holds no record for its job. `root/session.ts#resolveSession` rehydrates the session from the workflow record
     * (`root/rehydrate.ts`, replay-history on that edge) when `psid` names no record. Both absent unless both are well-formed.
     */
    pwf?: string;
    pdom?: string;
    /**
     * Review 7 JE-01: `pjob=<workflow id>` -- the /downloads card that opened the picker. Every card of a session links the same
     * `psid`; without this the picker restored the session's LATEST state (a later ZIP's pins on a single-frame card, and Convert
     * made another copy of that ZIP). `root/session.ts` restores that job's own mode, frame or pins, range and options.
     */
    pjob?: string;
}

/** §8 `FRAMEPICKER_OPENED.source`, as the closed set `psrc` is validated against. */
export const PICKER_OPEN_SOURCES: readonly PickerOpenSource[] = Object.freeze(['tab', 'nudge', 'nudge_thumb', 'time_link', 'downloads_link', 'url']);
/** IT2 (saas N2): the query key an opener writes its source under, and the /downloads link's value -- for `ui/file-list/frame-picker-card.ts#pickAnotherFrameHref`, so a bare `psid` can be read as a cold load. */
export const PICKER_PSRC_PARAM = 'psrc';
export const DOWNLOADS_LINK_SOURCE: PickerOpenSource = 'downloads_link';
/** Wave B: the query key that asks for an empty picker session (`PickerUrlState.pfresh`); its only value is `1`. */
export const PICKER_FRESH_PARAM = 'pfresh';
/** Editor re-open T4: the workflow to rehydrate from (`PickerUrlState.pwf`) and the edge that ran it (`pdom`). */
export const PICKER_WORKFLOW_PARAM = 'pwf';
export const PICKER_DOMAIN_PARAM = 'pdom';
/** Review 7 JE-01: the card's own job (`PickerUrlState.pjob`). */
export const PICKER_JOB_PARAM = 'pjob';

/** T4: a workflow uuid as the edge mints them (v4, hyphenated); anything else is not sent anywhere. */
const WORKFLOW_SHAPE = /^[A-Za-z0-9-]{8,64}$/;
export function isValidPickerWorkflowId(v: unknown): v is string {
    return typeof v === 'string' && WORKFLOW_SHAPE.test(v);
}

/**
 * T4: the edge a rehydrate may ask -- an xconvert host (bare or `https://`), or localhost for the dev loop. A crafted link must
 * not make the page POST a workflow uuid to a foreign host. Returns the host as given (scheme kept), or null.
 */
export function pickerProcessDomainOf(v: unknown): string | null {
    // Review 7 SHARED-URL C-2: ONE allow-list for every place a credential may go (`utils/edge-owner.ts`).
    return ourEdgeHost(v);
}

function openSourceOf(raw: string | null): PickerOpenSource | null {
    return raw && (PICKER_OPEN_SOURCES as readonly string[]).includes(raw) ? (raw as PickerOpenSource) : null;
}

/**
 * IT1 (saas #2): the nudge and the link know WHY the tab opens but the page's owner performs the write
 * (`openPickerTab()` without arguments, `file-convert.component.tsx`): they note the source here first, and the
 * next `openPickerTab` takes it. One-shot; a bare tab click after nothing noted is `'tab'`.
 */
let notedSource: PickerOpenSource | null = null;
export function notePickerOpenSource(source: PickerOpenSource): void {
    notedSource = source;
}

const STEPS: readonly string[] = ['edit', 'check'];

/**
 * The shape a picker session id must have before it is trusted.
 *
 * The next thing that happens to `psid` is a localStorage key (`FRAMEPICKER-<psid>`), so a
 * crafted or truncated value must not reach storage. Letters, digits, `-` and `_`, 4–64 long;
 * and never a value that would collide with persistence's own bookkeeping key
 * `FRAMEPICKER-LATEST-<pageSlug>` (§6.4). `state/persistence.ts` mints ids that satisfy this.
 */
const SID_SHAPE = /^[A-Za-z0-9_-]{4,64}$/;

export function isValidPickerSid(sid: unknown): sid is string {
    if (typeof sid !== 'string' || !SID_SHAPE.test(sid)) return false;
    return !/^LATEST(-|$)/i.test(sid);
}

export function isPickerTab(tab: string | null | undefined): boolean {
    return tab === PICKER_TAB_ID;
}

/**
 * `?tab=&psid=&pstep=` as the picker understands them.
 *
 * An unknown `pstep` degrades to `edit` rather than being honoured, and an invalid `psid`
 * reads as `null` — SO A CONSUMER MUST HANDLE `psid === null` FROM A URL THAT VISIBLY CARRIES
 * ONE, by minting a new session (or restoring the page's LATEST record), never by trusting the
 * raw parameter.
 *
 * The values are the ADDRESS BAR's, not an in-flight write's (`utils/route-state.ts`,
 * "READS DO NOT"): a caller that writes and reads back in the same tick sees the old URL, so
 * the picker keeps its own `psid` in state.
 */
export function readPickerUrl(search?: string): PickerUrlState {
    const q = readQuery(['tab', 'psid', 'pstep', 'psrc', PICKER_FRESH_PARAM, PICKER_WORKFLOW_PARAM, PICKER_DOMAIN_PARAM, PICKER_JOB_PARAM], search);
    const pwf = isValidPickerWorkflowId(q[PICKER_WORKFLOW_PARAM]) ? q[PICKER_WORKFLOW_PARAM] : null;
    const pdom = pwf ? pickerProcessDomainOf(q[PICKER_DOMAIN_PARAM]) : null;
    const pstep = q.pstep && STEPS.includes(q.pstep) ? (q.pstep as PickerStep) : 'edit';
    return {
        tab: q.tab,
        psid: isValidPickerSid(q.psid) ? q.psid : null,
        pstep,
        psrc: openSourceOf(q.psrc),
        ...(q[PICKER_FRESH_PARAM] === '1' ? { pfresh: true } : {}),
        ...(pwf && pdom ? { pwf, pdom } : {}),
        ...(isValidPickerSid(q[PICKER_JOB_PARAM]) ? { pjob: q[PICKER_JOB_PARAM] as string } : {}),
    };
}

/**
 * Open the tab. `{tab}` alone when the session is not known yet (the page base's tab click —
 * it does not know which session is being opened), both keys when it is (the `/downloads`
 * "Pick another frame" link, the nudge).
 */
export function openPickerTab(psid?: string | null, source?: PickerOpenSource): void {
    const withSession = !!psid && isValidPickerSid(psid);
    // IT1 (saas #2): the source rides as `psrc` -- the argument, else what the nudge / link noted, else the page's tab click.
    const psrc: PickerOpenSource = source ?? notedSource ?? (withSession ? 'downloads_link' : 'tab');
    notedSource = null;
    if (withSession) pushQuery({ tab: PICKER_TAB_ID, psid: psid as string, psrc });
    else pushQuery({ tab: PICKER_TAB_ID, psrc });
}

/** Wave B: `{ pfresh: null }` when the URL carries `pfresh` (it is consumed with the session's first id, or on close), else nothing. */
function freshCleared(): { [PICKER_FRESH_PARAM]?: null } {
    return typeof readQuery([PICKER_FRESH_PARAM])?.[PICKER_FRESH_PARAM] === 'string' ? { [PICKER_FRESH_PARAM]: null } : {};
}

/** `replace`, so Back from the picker does not stop on the URL from before the id was known. Consumes `psrc` (IT1). */
export function replacePickerSession(psid: string): void {
    if (!isValidPickerSid(psid)) return;
    replaceQuery({ psid, psrc: null, ...freshCleared() });
}

/** IT1 (saas #2): the open source has been counted -- take it off the address bar so a reload does not count again. */
export function consumePickerOpenSource(): void {
    replaceQuery({ psrc: null });
}

export function pushPickerStep(step: Exclude<PickerStep, 'edit'>): void {
    pushQuery({ pstep: step });
}

/** Leave the check step without closing the tab (the check's own "Back to the video"). */
export function clearPickerStep(): void {
    pushQuery({ pstep: null });
}

/**
 * IT1 (W3, saas #6): the picker page for another container -- `/convert-mov-to-jpg?tab=picker` from
 * `/convert-mp4-to-jpg` and `mov`. The site's 518 picker pages share the `convert-<from>-to-<to>` slug; a pathname of
 * another shape yields null (no link is better than a wrong one). The refusal sentence names this page; the link opens it.
 */
export function pickerPageHrefFor(pathname: string, ext: string): string | null {
    const m = /^(.*\/)?convert-[a-z0-9]+-to-([a-z0-9]+)\/?$/i.exec(pathname || '');
    const e = String(ext || '').toLowerCase().replace(/^\./, '');
    if (!m || !/^[a-z0-9]{1,8}$/.test(e)) return null;
    return `${m[1] ?? '/'}convert-${e}-to-${m[2].toLowerCase()}?tab=${PICKER_TAB_ID}`;
}

/** T4: `{ pwf: null, pdom: null }` for whichever of the two the URL carries, else nothing. */
function rehydrateCleared(): { [PICKER_WORKFLOW_PARAM]?: null; [PICKER_DOMAIN_PARAM]?: null } {
    const q = readQuery([PICKER_WORKFLOW_PARAM, PICKER_DOMAIN_PARAM]) ?? {};
    return {
        ...(typeof q[PICKER_WORKFLOW_PARAM] === 'string' ? { [PICKER_WORKFLOW_PARAM]: null } : {}),
        ...(typeof q[PICKER_DOMAIN_PARAM] === 'string' ? { [PICKER_DOMAIN_PARAM]: null } : {}),
    };
}

/**
 * T4: the rehydrate has settled -- the session now lives under `psid` (a fresh record, or none: the expired copy). A replace, so
 * Back skips the hand-off URL; `pwf`/`pdom` go with it, so a reload restores the record instead of asking the edge again.
 */
export function replacePickerRehydrated(psid: string): void {
    if (!isValidPickerSid(psid)) return;
    replaceQuery({ psid, psrc: null, [PICKER_WORKFLOW_PARAM]: null, [PICKER_DOMAIN_PARAM]: null });
}

/** Clears ONLY the picker's keys (`tab`, `psid`, `pstep`, and IT1's `psrc`). The Studio's four are never touched from here. */
export function closePickerTab(): void {
    pushQuery({ tab: null, psid: null, pstep: null, psrc: null, ...freshCleared(), ...rehydrateCleared() });
}
